import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { retryWhenOnline } from '@c-cdk/common';
import { Observable, Subject, takeUntil, tap } from 'rxjs';
import { SUPPORTED_IMAGE_EXTENSIONS } from '~backoffice/app/~shared/constants/global.constants';
import { KalturaThumbnail } from '~core/models/core.models';
import { VideoModule } from '../../video.module';
import { ContentService } from '~core/services/content.service';

@Component({
  selector: 'video-thumbnail-selector',
  imports: [VideoModule],
  templateUrl: './video-thumbnail-selector.component.html',
  styleUrls: ['./video-thumbnail-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VideoThumbnailSelectorComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Output() selection = new EventEmitter<string | File>();
  @Input() kalturaMediaId: string | null | undefined;
  @Input() preselectedMediaId: string | null | undefined;
  selectedMediaId: string | File | undefined;
  loadedImageSrc: string | undefined;

  contentService = inject(ContentService);
  cdRef = inject(ChangeDetectorRef);
  thumbnails$: Observable<KalturaThumbnail[]> | undefined;

  supportedFileExtensions = SUPPORTED_IMAGE_EXTENSIONS;
  fileControl = new FormControl();
  private _destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.initThumbnail();
    this.fileControl.valueChanges
      .pipe(
        takeUntil(this._destroy$),
        tap({
          next: value => {
            this.selectMedia(value, true);
          }
        })
      )
      .subscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('kalturaMediaId' in changes) {
      this.initThumbnail();
    }
  }

  ngOnDestroy() {
    this._destroy$.next();
    this._destroy$.complete();
  }

  selectMedia(mediaId: string | File, mediaIsFile = false): void {
    if (!mediaIsFile) {
      this.loadedImageSrc = undefined;
    }
    this.selectedMediaId = mediaId;
    this.selection.emit(mediaId);
  }

  initThumbnail(): void {
    if (this.kalturaMediaId) {
      this.thumbnails$ = this.contentService
        .listKalturaThumbnail(this.kalturaMediaId)
        .pipe(retryWhenOnline());
    }
  }

  onChange(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (files === null) {
      return;
    }
    const file = Object.values(files)[0];
    this.fileControl.setValue(file);
    const reader = new FileReader();
    reader.onload = () => {
      this.loadedImageSrc = reader.result as string;
      this.cdRef.detectChanges();
    };
    reader.readAsDataURL(file);
  }
}
